import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, TouchableOpacity} from 'react-native';
import {Text, Card, ActivityIndicator, Appbar} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation, DrawerActions} from '@react-navigation/native';
import api from '../../services/api/axiosInstance';
import {colors, spacing} from '../../theme';

const SummaryCard = ({value, label, color}) => (
  <Card style={styles.card}>
    <Card.Content>
      <Text variant="headlineMedium" style={[styles.cardNumber, {color}]}>{value}</Text>
      <Text variant="bodySmall" style={styles.cardLabel}>{label}</Text>
    </Card.Content>
  </Card>
);

const DashboardScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await api.get('/dashboard/summary');
      setSummary(response.data.data);
    } catch (e) {
      // silently fail on dashboard
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchSummary().finally(() => setLoading(false));
  }, [fetchSummary]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchSummary();
    setRefreshing(false);
  }, [fetchSummary]);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.Action icon="menu" onPress={() => navigation.dispatch(DrawerActions.openDrawer())} />
        <Appbar.Content title={t('dashboard.title')} />
      </Appbar.Header>
      <ScrollView
        style={styles.scrollView}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
        }>
      <View style={styles.cardRow}>
        <SummaryCard value={summary?.ordersToday ?? 0} label={t('dashboard.ordersToday')} color={colors.info} />
        <SummaryCard value={summary?.pendingOrders ?? 0} label={t('dashboard.pendingOrders')} color={colors.warning} />
      </View>

      <View style={styles.cardRow}>
        <SummaryCard value={summary?.readyForPickup ?? 0} label={t('dashboard.readyForPickup')} color={colors.success} />
        <SummaryCard value={summary?.overdueOrders ?? 0} label={t('dashboard.overdueOrders')} color={colors.error} />
      </View>

      {summary?.totalRevenue != null && (
        <Card style={styles.revenueCard}>
          <Card.Content style={styles.revenueContent}>
            <Text variant="bodyMedium" style={styles.revenueLabel}>{t('dashboard.monthlyRevenue')}</Text>
            <Text variant="headlineMedium" style={styles.revenueAmount}>
              {'\u20B9'}{Number(summary.totalRevenue).toLocaleString()}
            </Text>
            {summary.pendingPayments > 0 && (
              <Text variant="bodySmall" style={styles.pendingText}>
                Pending: {'\u20B9'}{Number(summary.pendingPayments).toLocaleString()}
              </Text>
            )}
          </Card.Content>
        </Card>
      )}

      <View style={styles.quickActions}>
        <Text variant="titleMedium" style={styles.sectionTitle}>{t('dashboard.quickActions')}</Text>
        <View style={styles.actionRow}>
          <TouchableOpacity
            style={[styles.actionTile, styles.actionTilePrimary]}
            activeOpacity={0.7}
            onPress={() => navigation.navigate('Orders', {screen: 'OrdersTab', params: {screen: 'OrderCreate'}})}>
            <Icon name="plus" size={22} color={colors.white} />
            <Text style={[styles.actionTileText, {color: colors.white}]}>{t('dashboard.newOrder')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.actionTile}
            activeOpacity={0.7}
            onPress={() => navigation.navigate('Customers', {screen: 'CustomerForm'})}>
            <Icon name="account-plus" size={22} color={colors.primary} />
            <Text style={styles.actionTileText}>{t('dashboard.newCustomer')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.actionTile}
            activeOpacity={0.7}
            onPress={() => navigation.navigate('Expenses', {screen: 'ExpenseForm'})}>
            <Icon name="cash-plus" size={22} color={colors.primary} />
            <Text style={styles.actionTileText}>{t('dashboard.addExpense')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  scrollView: {flex: 1, paddingTop: spacing.md},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  appbar: {backgroundColor: colors.primary},
  cardRow: {flexDirection: 'row', paddingHorizontal: spacing.md, gap: spacing.sm},
  card: {flex: 1, marginBottom: spacing.sm, backgroundColor: colors.surface, borderRadius: 12},
  cardNumber: {fontSize: 32, fontWeight: 'bold'},
  cardLabel: {color: colors.textSecondary, marginTop: 4},
  revenueCard: {margin: spacing.md, backgroundColor: colors.primary, borderRadius: 12},
  revenueContent: {alignItems: 'center', paddingVertical: spacing.md},
  revenueLabel: {color: colors.white, opacity: 0.8},
  revenueAmount: {color: colors.white, fontWeight: 'bold', marginTop: 4},
  pendingText: {color: colors.white, opacity: 0.7, marginTop: 4},
  quickActions: {padding: spacing.md},
  sectionTitle: {fontWeight: '600', color: colors.text, marginBottom: spacing.sm},
  actionRow: {flexDirection: 'row', gap: spacing.sm},
  actionTile: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
  },
  actionTilePrimary: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  actionTileText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text,
    flexShrink: 1,
  },
});

export default DashboardScreen;
